import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactMapGl, { AttributionControl, Marker, NavigationControl } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import { detailLocationMap, getLocation } from 'redux-api/slices/locationMapSlice';
import { REQUEST_STATUSES } from 'common/constants';
import { ReactComponent as Mark } from 'assets/icons/marker.svg';
import iconPhone from 'assets/icons/home/hmIconPhone.svg';
import iconMail from 'assets/icons/home/hmIconMail.svg';
import iconPin from 'assets/icons/home/hmIconPin.svg';
import 'mapbox-gl/dist/mapbox-gl.css';
import classes from './HomeContacts.module.scss';


const STYLE_MAP = 'mapbox://styles/mratick/clgup5gmm005g01pg32w6gcfg';
const TOKEN = 'pk.eyJ1IjoibXJhdGljayIsImEiOiJjbGd1cDlxZHMxNm5vM2ZsOGw3M2ZwNWVuIn0.mzMU9BGHqb7NllTf1TmPeA';

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

// Карта во весь блок не должна перехватывать прокрутку страницы: колесо листает
// страницу, масштаб — Ctrl/⌘ + колесо или кнопки «+ −»; на телефоне карта
// двигается двумя пальцами, одним — прокручивается страница.
const MAP_LOCALE = {
  'ScrollZoomBlocker.CtrlMessage': 'Чтобы изменить масштаб, прокручивайте карту с зажатой Ctrl',
  'ScrollZoomBlocker.CmdMessage': 'Чтобы изменить масштаб, прокручивайте карту с зажатой ⌘',
  'TouchPanBlocker.Message': 'Чтобы переместить карту, проведите по ней двумя пальцами',
  'NavigationControl.ZoomIn': 'Приблизить',
  'NavigationControl.ZoomOut': 'Отдалить',
};

// Телефоны и адреса приходят списками объектов — берём из них значения.
const listValues = (items = []) => items.map((item) => Object.values(item)[ 0 ]);

const HomeContacts = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { location, locationStatus } = useSelector(detailLocationMap);

  const [ activeCity, setActiveCity ] = useState(0);
  // Карточку можно свернуть, чтобы посмотреть карту целиком.
  const [ isCardOpen, setIsCardOpen ] = useState(true);
  const [ options, setOptions ] = useState({
    latitude: 42.90071019998644,
    longitude: 74.5936226263244,
    zoom: 10,
  });

  useEffect(() => {
    dispatch(getLocation());
    return () => null;
  }, [ dispatch ]);

  const onMapMove = (event) => {
    const { latitude, longitude, zoom } = event.viewState;
    setOptions({ latitude, longitude, zoom: zoom || 12 });
  };

  const selectCity = (item, i) => {
    setActiveCity(i);
    setOptions({
      latitude: item.point[ 0 ].latitude,
      longitude: item.point[ 0 ].longitude,
      zoom: 14,
    });
  };

  if (locationStatus !== REQUEST_STATUSES.SUCCEEDED || !location?.length) return null;
  const current = location[ activeCity ];

  return (
    <section className={classes.contacts}>
      <div className={classes.map}>
        <ReactMapGl
          {...options}
          mapboxAccessToken={TOKEN}
          style={{ width: '100%', height: '100%' }}
          mapStyle={STYLE_MAP}
          cooperativeGestures
          locale={MAP_LOCALE}
          attributionControl={false}
          onDrag={onMapMove}
          onZoom={onMapMove}
        >
          <NavigationControl position="top-right" showCompass={false} />
          {/* Подпись mapbox обязательна, но свёрнута в значок «i». */}
          <AttributionControl position="bottom-right" compact />
          {location.map((city) =>
            city.point.map((marker, i) => (
              <Marker
                key={`${city.city}-${i}`}
                latitude={marker.latitude}
                longitude={marker.longitude}
                color="yellow"
              >
                <Mark />
              </Marker>
            ))
          )}
        </ReactMapGl>
      </div>

      <div className={classes.container}>
        {/* Свёрнутое состояние: кнопка поверх карты возвращает карточку. */}
        <button
          type="button"
          className={`${classes.reopen} ${isCardOpen ? '' : classes.reopenShown}`}
          onClick={() => setIsCardOpen(true)}
          aria-hidden={isCardOpen}
          tabIndex={isCardOpen ? -1 : 0}
        >
          <img src={iconPhone} alt="" />
          {t('Contacts')}
        </button>

        <div
          className={`${classes.card} ${isCardOpen ? '' : classes.cardHidden}`}
          aria-hidden={!isCardOpen}
        >
          <div className={classes.cardHead}>
            <h2 className={classes.title}>{t('Contacts')}</h2>
            <button
              type="button"
              className={classes.collapse}
              onClick={() => setIsCardOpen(false)}
              aria-label="Скрыть контакты и показать карту"
              tabIndex={isCardOpen ? 0 : -1}
            >
              <span />
            </button>
          </div>

          <div className={classes.cities}>
            {location.map((item, i) => (
              <button
                type="button"
                className={`${classes.city} ${i === activeCity ? classes.cityActive : ''}`}
                key={item.city}
                onClick={() => selectCity(item, i)}
              >
                {item.city}
              </button>
            ))}
          </div>

          <div className={classes.groups}>
            <div className={classes.group}>
              <p className={classes.groupTitle}>
                <img src={iconPhone} alt="" />
                {t('PhoneNumber')}
              </p>
              <div className={classes.groupBody}>
                {listValues(current?.phones).map((phone) => (
                  <a className={classes.link} href={`tel:${phone}`} key={phone}>{phone}</a>
                ))}
              </div>
            </div>

            <div className={classes.group}>
              <p className={classes.groupTitle}>
                <img src={iconMail} alt="" />
                {t('Email')}
              </p>
              <div className={classes.groupBody}>
                {listValues(current?.mails).map((mail) => (
                  <a className={classes.link} href={`mailto:${mail}`} key={mail}>{mail}</a>
                ))}
              </div>
            </div>

            <div className={classes.group}>
              <p className={classes.groupTitle}>
                <img src={iconPin} alt="" />
                {t('Address')}
              </p>
              <div className={classes.groupBody}>
                {listValues(current?.addresses).map((address) => (
                  <p className={classes.value} key={address}>{address}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeContacts;
